/**
 * The small store for the live stream (design 12.1).
 *
 * The shell needs the connection status NOW (for the connection indicator) but the stream client
 * that drives it is a LATER task (11.3, the WebSocket/SSE client with resume and fallback). So this
 * file is just the store shape: an external store with a connection-state machine and a place for
 * the client to publish what it is doing. 11.3 imports this store and calls `setConnection` /
 * `noteActivity` from the real client; until then the store sits in its initial `disconnected`
 * state and the indicator reads it honestly.
 *
 * It is a plain observable store (not React state) so non-React code — the stream client — can write
 * to it, and any number of components can subscribe through `useSyncExternalStore`. There is one
 * store per app, created at bootstrap and provided through React context (see `live-stream.tsx`).
 */

/**
 * The connection-state machine the indicator renders (FR-13 AC3: the UI displays the connection
 * state). `connecting` covers the initial connect and every reconnect/resume attempt; `error` is a
 * terminal-ish state the client sets when it has given up for now (it may still retry and move back
 * to `connecting`).
 */
export type ConnectionState = "disconnected" | "connecting" | "connected" | "error";

/** How the live stream is carried, once the client (11.3) has chosen. */
export type StreamTransport = "websocket" | "sse" | null;

/** A snapshot of the live-stream store. Immutable; a new object is produced on every change. */
export interface LiveStreamSnapshot {
  /** The connection-state machine value. */
  readonly connection: ConnectionState;
  /** The transport in use, or `null` before the client has connected. */
  readonly transport: StreamTransport;
  /** A human-readable reason for the current state (e.g. "sockets blocked, using SSE"), or null. */
  readonly detail: string | null;
  /** `performance.now()`-style timestamp (ms) of the last inbound activity, or null if none yet. */
  readonly lastActivityAt: number | null;
  /**
   * The resume token the client holds (opaque). Not read by the shell; exposed so the indicator can
   * show "resuming" vs "fresh" once 11.3 wires it. `null` until the client has one.
   */
  readonly resumeToken: string | null;
}

const INITIAL: LiveStreamSnapshot = {
  connection: "disconnected",
  transport: null,
  detail: null,
  lastActivityAt: null,
  resumeToken: null,
};

type Listener = () => void;

/**
 * The observable live-stream store. Created once per app. `getSnapshot` returns a stable reference
 * between changes so `useSyncExternalStore` does not loop. The stream client (11.3) owns the
 * mutators; the shell only reads.
 */
export interface LiveStreamStore {
  getSnapshot(): LiveStreamSnapshot;
  subscribe(listener: Listener): () => void;
  /** Set the connection state, optionally with a transport and human-readable detail. */
  setConnection(
    connection: ConnectionState,
    options?: { transport?: StreamTransport; detail?: string | null },
  ): void;
  /** Record that an inbound frame arrived (the indicator can show staleness from this). */
  noteActivity(at: number): void;
  /** Store the opaque resume token the client holds (or `null` to clear it). */
  setResumeToken(token: string | null): void;
}

export function createLiveStreamStore(): LiveStreamStore {
  let snapshot: LiveStreamSnapshot = INITIAL;
  const listeners = new Set<Listener>();

  const emit = (next: LiveStreamSnapshot): void => {
    snapshot = next;
    for (const listener of listeners) listener();
  };

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setConnection: (connection, options) => {
      emit({
        ...snapshot,
        connection,
        // `transport` and `detail` are only overwritten when explicitly provided, so a bare state
        // change (e.g. connected -> connecting on a drop) keeps the known transport.
        transport: options?.transport ?? snapshot.transport,
        detail: options?.detail === undefined ? snapshot.detail : options.detail,
      });
    },
    noteActivity: (at) => {
      emit({ ...snapshot, lastActivityAt: at });
    },
    setResumeToken: (token) => {
      emit({ ...snapshot, resumeToken: token });
    },
  };
}
